import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  useTheme,
  alpha,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  LocalHospital as HospitalIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Block as BlockIcon,
  CalendarToday as CalendarIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material';

const ControlMedico = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  const controlMedicoData = [
    {
      id: 1,
      empleado: 'Juan Rodríguez',
      legajo: 'PL-001',
      area: 'Perforación',
      aptoMedico: 'Vigente',
      fechaApto: '15/06/2025',
      vencimiento: '15/06/2026',
      tipoExamen: 'Periódico',
      observaciones: 'Sin restricciones',
    },
    {
      id: 2,
      empleado: 'Pedro García',
      legajo: 'PL-002',
      area: 'Workover',
      aptoMedico: 'Vencido',
      fechaApto: '20/01/2025',
      vencimiento: '20/01/2026',
      tipoExamen: 'Periódico',
      observaciones: 'BLOQUEADO - Requiere examen',
    },
    {
      id: 3,
      empleado: 'María López',
      legajo: 'PL-003',
      area: 'Administración',
      aptoMedico: 'Vigente',
      fechaApto: '10/03/2025',
      vencimiento: '10/03/2026',
      tipoExamen: 'Periódico',
      observaciones: 'Apto para trabajo administrativo',
    },
    {
      id: 4,
      empleado: 'Carlos Mendoza',
      legajo: 'PL-004',
      area: 'Seguridad',
      aptoMedico: 'Por vencer',
      fechaApto: '15/02/2025',
      vencimiento: '15/02/2026',
      tipoExamen: 'Periódico',
      observaciones: 'Vence en 12 días',
    },
    {
      id: 5,
      empleado: 'Ana Martínez',
      legajo: 'PL-005',
      area: 'Producción',
      aptoMedico: 'Vigente',
      fechaApto: '01/09/2025',
      vencimiento: '01/09/2026',
      tipoExamen: 'Ingreso',
      observaciones: 'Sin restricciones',
    },
  ];

  const getEstadoConfig = (estado) => {
    switch (estado) {
      case 'Vigente':
        return { color: 'success', icon: <CheckIcon />, label: 'Vigente' };
      case 'Por vencer':
        return { color: 'warning', icon: <WarningIcon />, label: 'Por vencer' };
      case 'Vencido':
        return { color: 'error', icon: <BlockIcon />, label: 'VENCIDO' };
      default:
        return { color: 'default', icon: null, label: estado };
    }
  };

  const resumenStats = [
    { label: 'Aptos Vigentes', value: 265, icon: <CheckIcon />, color: theme.palette.success.main },
    { label: 'Por Vencer (30 días)', value: 16, icon: <WarningIcon />, color: theme.palette.warning.main },
    { label: 'Vencidos', value: 3, icon: <BlockIcon />, color: theme.palette.error.main },
    { label: 'Exámenes Programados', value: 8, icon: <CalendarIcon />, color: theme.palette.info.main },
  ];

  return (
    <Box className="fade-in">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary">
            Control Médico
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Aptos médicos, exámenes periódicos y certificados
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<CalendarIcon />}>
            Programar Exámenes
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
            Cargar Apto Médico
          </Button>
        </Box>
      </Box>

      {/* Alerta Importante */}
      <Alert severity="error" sx={{ mb: 3 }} icon={<PersonOffIcon />}>
        <Typography variant="body2">
          <strong>⚠️ BLOQUEO ACTIVO:</strong> 3 empleados tienen apto médico vencido y no pueden ser asignados 
          a ninguna tarea de campo hasta regularizar su situación.
        </Typography>
      </Alert>

      {/* Resumen */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {resumenStats.map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: alpha(stat.color, 0.1), color: stat.color }}>
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={stat.color}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabla */}
      <Card>
        <CardContent>
          <Tabs value={selectedTab} onChange={(e, v) => setSelectedTab(v)} sx={{ mb: 3 }}>
            <Tab label="Todos" />
            <Tab label="Vigentes (265)" />
            <Tab label="Por Vencer (16)" />
            <Tab label="Vencidos (3)" />
          </Tabs>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell>Empleado</TableCell>
                  <TableCell>Área</TableCell>
                  <TableCell>Tipo Examen</TableCell>
                  <TableCell>Fecha Apto</TableCell>
                  <TableCell>Vencimiento</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Observaciones</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {controlMedicoData.map((row) => {
                  const estadoConfig = getEstadoConfig(row.aptoMedico);
                  return (
                    <TableRow 
                      key={row.id} 
                      hover
                      sx={{
                        bgcolor: row.aptoMedico === 'Vencido' ? alpha(theme.palette.error.main, 0.05) : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main, fontSize: '0.75rem' }}>
                            {row.empleado.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{row.empleado}</Typography>
                            <Typography variant="caption" color="text.secondary">{row.legajo}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{row.area}</TableCell>
                      <TableCell>
                        <Chip label={row.tipoExamen} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{row.fechaApto}</TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          fontWeight={500}
                          color={row.aptoMedico === 'Vencido' ? 'error.main' : 'text.primary'}
                        >
                          {row.vencimiento}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={estadoConfig.icon}
                          label={estadoConfig.label}
                          size="small"
                          color={estadoConfig.color}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="caption" 
                          color={row.aptoMedico === 'Vencido' ? 'error.main' : 'text.secondary'}
                        >
                          {row.observaciones}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="outlined" color={row.aptoMedico === 'Vencido' ? 'error' : 'primary'}>
                          {row.aptoMedico === 'Vencido' ? 'Gestionar' : 'Ver'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Dialog Cargar Apto */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HospitalIcon color="primary" />
            Cargar Apto Médico
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Empleado</InputLabel>
                <Select label="Empleado">
                  <MenuItem value="1">Juan Rodríguez - PL-001</MenuItem>
                  <MenuItem value="2">Pedro García - PL-002</MenuItem>
                  <MenuItem value="3">María López - PL-003</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Examen</InputLabel>
                <Select label="Tipo de Examen">
                  <MenuItem value="ingreso">Preocupacional (Ingreso)</MenuItem>
                  <MenuItem value="periodico">Periódico</MenuItem>
                  <MenuItem value="egreso">Egreso</MenuItem>
                  <MenuItem value="especial">Especial</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha del Examen"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Fecha de Vencimiento"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Resultado</InputLabel>
                <Select label="Resultado">
                  <MenuItem value="apto">Apto</MenuItem>
                  <MenuItem value="apto_restriccion">Apto con Restricciones</MenuItem>
                  <MenuItem value="no_apto">No Apto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Centro Médico" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Observaciones" multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                Adjuntar Certificado (PDF)
                <input type="file" hidden accept=".pdf" />
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ControlMedico;
